<script setup lang="ts">
  import { ref } from "vue";
  import { Trash2 } from "lucide-vue-next";
  import { POPULATION_COLOR_PALETTES, POPULATION_PALETTE_NAMES, POPULATION_SHADES } from "@/lib/chartPalettes";
  import { SheetFooter } from "@/components/ui/sheet";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import FilterAccordion from "./FilterAccordion.vue";

  defineProps<{
    color: string | null;
    filters: Record<string, Record<string, boolean>>;
    cards: unknown[];
    canSave: boolean;
    canDelete: boolean;
    saveLabel: string;
  }>();

  const name = defineModel<string>("name", { required: true });

  const emit = defineEmits<{
    "update:color": [color: string | null];
    "toggle-filter": [questionId: string, code: string];
    save: [];
    delete: [];
  }>();

  const colorPickerOpen = ref(false);

  const pickColor = (value: string | null) => {
    emit("update:color", value);
    colorPickerOpen.value = false;
  };
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
    <slot />

    <div class="space-y-1.5">
      <Label class="text-xs">Name</Label>
      <Input
        v-model="name"
        placeholder="Population name..."
        class="h-9" />
    </div>

    <div class="space-y-1.5">
      <Label class="text-xs">Color</Label>
      <button
        aria-label="Change population color"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border transition-colors hover:bg-muted"
        :class="colorPickerOpen ? 'bg-muted' : ''"
        @click="colorPickerOpen = !colorPickerOpen">
        <span
          v-if="color"
          class="h-4 w-4 rounded-full"
          :style="{ backgroundColor: color }" />
        <span
          v-else
          class="text-3xs font-medium text-muted-foreground">
          Auto
        </span>
      </button>

      <div
        v-if="colorPickerOpen"
        class="flex flex-col gap-1 rounded-md border bg-muted/20 p-2">
        <div class="mb-0.5 flex items-center gap-1">
          <button
            class="rounded-sm border px-2 py-0.5 text-xs font-medium transition-all"
            :class="!color ? 'ring-2 ring-offset-1 ring-foreground/40 bg-muted' : 'hover:bg-muted'"
            @click="pickColor(null)">
            Auto
          </button>
        </div>
        <div
          v-for="palette in POPULATION_PALETTE_NAMES"
          :key="palette"
          class="flex gap-0.5">
          <button
            v-for="shade in POPULATION_SHADES"
            :key="shade"
            class="h-4 w-4 rounded-sm transition-all"
            :class="
              color === POPULATION_COLOR_PALETTES[palette][shade]
                ? 'ring-2 ring-offset-1 ring-foreground/40 scale-125'
                : 'hover:scale-110'
            "
            :style="{ backgroundColor: POPULATION_COLOR_PALETTES[palette][shade] }"
            @click="pickColor(POPULATION_COLOR_PALETTES[palette][shade])" />
        </div>
      </div>
    </div>

    <div class="flex min-h-0 flex-1 flex-col gap-1.5">
      <Label class="text-xs">Filters</Label>
      <FilterAccordion
        :model-value="filters"
        :cards="cards"
        @toggle="(questionId: string, code: string) => emit('toggle-filter', questionId, code)" />
    </div>
  </div>

  <SheetFooter class="flex-row items-center gap-2 border-t px-4 pt-3">
    <Button
      v-if="canDelete"
      variant="ghost"
      class="text-destructive hover:bg-destructive/10 hover:text-destructive"
      @click="emit('delete')">
      <Trash2 class="mr-1 h-3.5 w-3.5" />
      Delete
    </Button>

    <Button
      class="ml-auto"
      :disabled="!canSave"
      @click="emit('save')">
      {{ saveLabel }}
    </Button>
  </SheetFooter>
</template>
